import {
  ChangeDetectionStrategy,
  computed,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormBuilder,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  CdkDrag,
  CdkDropList,
  CdkDragHandle,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { ProductDetailFormControlsType } from '@app/utils/interfaces/product-detail-form-controls.interface';
import {
  ProductDetailDataType,
  ProductDetailUnit,
} from '@app/utils/enums/inventory-table-columns.enum';
import { TABLE_HEADERS } from '@app/utils/constants/product-details.constants';
import { InventoryService } from '@app/services/inventory/inventory.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { InventoryItem, InventoryItemDetail } from '@app/utils/interfaces/inventory-item.interface';
import { HighlightDirective } from './directives/highlight.directive';
import { stringToEnum } from '@app/utils/helpers/string-to-enum/string-to-enum.helper';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { sortInventoryItemsByNameOrUpcScore } from '@app/utils/helpers/sort-inventory-items-by-name-or-upc-score/sort-inventory-items-by-name-or-upc-score';
import { REGEX } from '@app/utils/constants/regex.constants';
import { unitValidator } from './validators/unit-validator';
import { FindFirstErrorKeyFromControlPipe } from '@app/utils/pipes/find-first-error-key-from-control/find-first-error-key-from-control.pipe';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-product-details-section',
  standalone: true,
  imports: [
    TranslateModule,
    IconComponent,
    MatFormFieldModule,
    MatInput,
    MatSelectModule,
    ReactiveFormsModule,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
    MatCheckboxModule,
    HighlightDirective,
    AsyncPipe,
    FindFirstErrorKeyFromControlPipe,
  ],
  templateUrl: './product-details-section.component.html',
  styleUrl: './product-details-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailsSectionComponent {
  public readonly currentDetails = input.required<InventoryItemDetail[]>();
  public readonly itemId = input.required<string>();
  public readonly detailsFormArray =
    input.required<FormArray<FormGroup<ProductDetailFormControlsType>>>();

  protected readonly HEADERS = TABLE_HEADERS;
  protected readonly ICON_NAMES = IconName;
  protected readonly DATA_TYPES = Object.values(ProductDetailDataType);
  protected readonly UNITS = Object.values(ProductDetailUnit);
  protected readonly PRODUCT_DETAIL_DATA_TYPE = ProductDetailDataType;
  protected readonly MAX_NUMBER_OF_DETAILS = 20;
  protected readonly isApplyFromOtherChecked = signal(false);
  protected readonly searchInputFormControl = new FormControl('', { nonNullable: true });
  protected readonly query = signal('');
  protected readonly isOtherDetailSelected = signal(false);
  private readonly fb = inject(FormBuilder);
  protected readonly detailsRenderTrigger = signal(0);
  protected readonly detailRows = computed(() => {
    this.detailsRenderTrigger();
    return this.detailsFormArray().controls;
  });
  protected readonly detailsCount = computed(() => {
    this.detailsRenderTrigger();
    return this.detailsFormArray().length;
  });
  private readonly syncedItemId = signal<string | null>(null);

  private readonly inventoryService = inject(InventoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly matDialog = inject(MatDialog);

  protected readonly foundItems = toSignal(
    this.searchInputFormControl.valueChanges.pipe(
      debounceTime(500),
      map((value) => value.trim()),
      distinctUntilChanged(),
      tap((query) => {
        this.query.set(query);
        this.isOtherDetailSelected.set(false);
      }),
      switchMap((query) => {
        if (!query) {
          return of([]);
        }

        return this.inventoryService.searchInventoryItems(query).pipe(
          map(({ data: { items } }) => sortInventoryItemsByNameOrUpcScore(items, query)),
          catchError(() => of([])),
        );
      }),
    ),
    { initialValue: [] },
  );

  constructor() {
    effect(
      (onCleanup) => {
        const details = this.detailsFormArray();
        const subscription = details.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.notifyDetailsChanged());

        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const details = this.detailsFormArray();
        const currentItemId = this.itemId();
        const inputDetails = this.currentDetails();

        if (this.syncedItemId() !== currentItemId) {
          details.clear();
          this.notifyDetailsChanged();
          this.syncedItemId.set(currentItemId);
          if (inputDetails.length > 0) {
            inputDetails.forEach((detail) =>
              details.push(this.createProductDetailFormGroup(detail)),
            );
            this.notifyDetailsChanged();
          }
          this.loadInitialDetailsByItemId(currentItemId);
          return;
        }

        if (details.length === 0 && inputDetails.length > 0) {
          inputDetails.forEach((detail) => details.push(this.createProductDetailFormGroup(detail)));
          this.notifyDetailsChanged();
        }
      },
      { allowSignalWrites: true },
    );
  }

  protected draggableDrop(event: CdkDragDrop<FormGroup<ProductDetailFormControlsType>[]>) {
    moveItemInArray(this.detailsFormArray().controls, event.previousIndex, event.currentIndex);
    this.notifyDetailsChanged();
  }

  protected deleteOption(optionIndex: number): void {
    this.detailsFormArray().removeAt(optionIndex);
    this.notifyDetailsChanged();
  }

  protected addProductDetail(): void {
    this.detailsFormArray().push(this.createProductDetailFormGroup());
    this.notifyDetailsChanged();
  }

  protected applyDetailsFromOtherProduct(isChecked: boolean): void {
    this.isApplyFromOtherChecked.set(isChecked);
  }

  protected loadDetails(item: InventoryItem): void {
    this.inventoryService
      .getInventoryItemDetails(item.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((items: InventoryItemDetail[]) => [...items].sort((a, b) => a.order - b.order)),
        map((sortedItems: InventoryItemDetail[]) =>
          sortedItems.map((itemDetail) => this.createProductDetailFormGroup(itemDetail)),
        ),
      )
      .subscribe({
        next: (itemGroups: FormGroup<ProductDetailFormControlsType>[]) => {
          itemGroups.forEach((itemGroup) => this.detailsFormArray().push(itemGroup));
          this.notifyDetailsChanged();

          this.query.set(item.productName);
          this.searchInputFormControl.setValue(this.query(), { emitEvent: false });
          this.isOtherDetailSelected.set(true);
        },
        error: () => {
          this.matDialog.open(StatusModalComponent, {
            data: {
              title: 'inventory.product-details.apply-from-other.error-title',
              description: 'inventory.product-details.apply-from-other.error-description',
            },
          });
        },
      });
  }

  private notifyDetailsChanged(): void {
    this.detailsRenderTrigger.update((value) => value + 1);
  }

  private loadInitialDetailsByItemId(itemId: string): void {
    this.inventoryService
      .getInventoryItemById(itemId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((item) => item.productDetails ?? []),
        switchMap((detailsFromItem) => {
          if (detailsFromItem.length > 0) {
            return of(detailsFromItem);
          }

          return this.inventoryService
            .getInventoryItemDetails(itemId)
            .pipe(catchError(() => of([])));
        }),
        map((items: InventoryItemDetail[]) => [...items].sort((a, b) => a.order - b.order)),
        catchError(() => of([])),
      )
      .subscribe({
        next: (sortedItems: InventoryItemDetail[]) => {
          const details = this.detailsFormArray();
          if (sortedItems.length === 0 && details.length > 0) {
            return;
          }

          details.clear();
          sortedItems.forEach((detail) => details.push(this.createProductDetailFormGroup(detail)));
          this.notifyDetailsChanged();
        },
      });
  }

  private createProductDetailFormGroup(
    detail?: InventoryItemDetail,
  ): FormGroup<ProductDetailFormControlsType> {
    const nameControl = this.fb.nonNullable.control(detail?.name ?? '', [
      Validators.required,
      Validators.pattern(REGEX.productDetails.name),
      Validators.minLength(2),
      Validators.maxLength(50),
    ]);

    const typeControl = this.fb.nonNullable.control(
      stringToEnum(ProductDetailDataType, detail?.type ?? '') ?? ProductDetailDataType.STRING,
      [Validators.required],
    );

    const unitControl = this.fb.nonNullable.control(
      stringToEnum(ProductDetailUnit, detail?.unit ?? '') ?? ProductDetailUnit.EMPTY,
    );

    const valueControl = this.fb.nonNullable.control(detail?.value ?? '', [Validators.required]);

    const group = this.fb.group<ProductDetailFormControlsType>({
      name: nameControl,
      type: typeControl,
      unit: unitControl,
      value: valueControl,
    });

    const unitValidatorRef = unitValidator();
    const decimalValidatorRef = Validators.pattern(REGEX.productDetails.digitValue);
    const stringValidatorRef = [
      Validators.pattern(REGEX.productDetails.stringValue),
      Validators.maxLength(50),
    ];

    typeControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        startWith(typeControl.value),
      )
      .subscribe({
        next: (type: ProductDetailDataType) => {
          if (type == ProductDetailDataType.NUMBER) {
            unitControl.enable();
            unitControl.addValidators(unitValidatorRef);
            valueControl.addValidators(decimalValidatorRef);
          } else {
            unitControl.removeValidators(unitValidatorRef);
            unitControl.setValue(ProductDetailUnit.EMPTY);
            unitControl.disable();
            valueControl.removeValidators(decimalValidatorRef);

            type == ProductDetailDataType.STRING
              ? valueControl.addValidators(stringValidatorRef)
              : valueControl.removeValidators(stringValidatorRef);
          }

          group.markAllAsTouched();

          unitControl.updateValueAndValidity();
          valueControl.updateValueAndValidity();
        },
      });

    return group;
  }
}
